import { ThemeOptions } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { merge } from 'lodash';
import * as React from 'react';

import { useFullscreenContext } from '@/components/Fullscreen/fullscreen.context';

import { COLORS } from '../tokens/colors';
import { COMMON_THEME_PROPERTIES } from './common';

// The complete theme object, along with defined defaults, is available
// at: https://mui.com/material-ui/customization/default-theme/?expand-path=$.palette

// The container element for popper components when in fullscreen mode
function container() {
  return document.fullscreenElement ?? document.body;
}

export function getDarkTheme(isFullscreen = false) {
  // Override typography colors for dark theme
  const darkTextColors = {
    'title-xl': {
      color: '#ffffff',
    },
    'title-l': {
      color: '#ffffff',
    },
    'title-m': {
      color: '#ffffff',
    },
    'title-s': {
      color: '#ffffff',
    },
    'title-xs': {
      color: '#ffffff',
    },
    'title-serif-m': {
      color: '#ffffff',
    },
    'body-m': {
      color: COLORS.NAVY[300],
    },
    'body-s': {
      color: COLORS.NAVY[300],
    },
    'body-xs': {
      color: COLORS.NAVY[300],
    },
    'eyebrow-xs': {
      color: COLORS.NAVY[300],
    },
    h1: {
      color: '#ffffff',
    },
    h2: {
      color: '#ffffff',
    },
    h2light: {
      color: '#C8D3DF',
    },
    h3: {
      color: '#ffffff',
    },
    h4: {
      color: '#ffffff',
    },
    h5: {
      color: '#ffffff',
    },
    h6: {
      color: '#ffffff',
    },
    subtitle1: {
      color: COLORS.NAVY[300],
    },
    subtitle2: {
      color: COLORS.NAVY[300],
    },
    body1: {
      color: COLORS.NAVY[900],
    },
    body2: {
      color: COLORS.NAVY[900],
    },
    label: {
      color: COLORS.NAVY[300],
    },
    label2: {
      color: COLORS.NAVY[300],
    },
  };

  // Override the container element for popper components when in fullscreen mode because
  // the default container is not guaranteed to be included in the fullscreen frame
  const fullscreenOverrides: ThemeOptions = isFullscreen
    ? {
        components: {
          MuiMenu: {
            defaultProps: {
              container,
            },
          },
          MuiTooltip: {
            defaultProps: {
              PopperProps: {
                container,
              },
            },
          },
          MuiPopover: {
            defaultProps: {
              container,
            },
          },
          MuiDialog: {
            defaultProps: {
              container,
            },
          },
          MuiTextField: {
            styleOverrides: {
              root: {
                color: COLORS.NAVY[800],
              },
            },
          },
        },
      }
    : {};

  const baseTheme = merge(
    {},
    COMMON_THEME_PROPERTIES,
    { typography: darkTextColors },
    fullscreenOverrides
  );

  return createTheme(baseTheme, {
    palette: {
      mode: 'dark',
      primary: {
        dark: COLORS.NAVY[900],
        main: COLORS.NAVY[700],
        light: COLORS.NAVY[600],
        contrastText: '#ffffff',
      },
      secondary: {
        dark: COLORS.BLUE[900],
        main: COLORS.BLUE[700],
        light: COLORS.GRAY[100],
        contrastText: '#ffffff',
      },
      buttons: {
        dark: COLORS.NAVY[300],
        main: COLORS.NAVY[400],
        light: COLORS.NAVY[600],
        contrastText: '#ffffff',
      },
      links: {
        dark: COLORS.NAVY[300],
        main: COLORS.NAVY[400],
        light: COLORS.NAVY[600],
        contrastText: '#ffffff',
      },
      education: {
        main: COLORS.FUNCTIONAL.WARNING[400],
      },
      background: {
        default: COLORS.GRAY[50],
        paper: '#ffffff',
      },
      text: {
        primary: COLORS.NAVY[50],
        secondary: COLORS.NAVY[100],
      },
    },
  });
}

export function useLuminaryDarkTheme() {
  const { isFullscreen } = useFullscreenContext();
  return getDarkTheme(isFullscreen);
}

export function LuminaryDarkThemeProvider({
  children,
}: React.PropsWithChildren<unknown>) {
  const theme = useLuminaryDarkTheme();
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
